import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminDetailedCategories,
  saveDetailedCategory,
  deleteDetailedCategory,
  type DetailedCategoryPayload,
} from "@/services/adminService";
import type { AdminDetailedCategory } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const EMPTY: DetailedCategoryPayload = {
  letter: "A",
  name: "",
  businessCount: 0,
  sortOrder: 1,
  isActive: true,
};

/**
 * Admin — Detailed A–Z categories.
 *
 * Manages the `detailed_categories` table (the "380+ detailed categories" A–Z
 * index). Lets an admin rename, re-letter, reorder, add or hide entries without
 * touching code. Hidden entries stop appearing on the public A–Z page.
 */
export default function DetailedCategoriesSection() {
  const { accessToken } = useAuth();

  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminDetailedCategories(accessToken),
    [] as AdminDetailedCategory[],
    [accessToken],
  );

  const [search, setSearch]     = useState("");
  const [letter, setLetter]     = useState("All");
  const [busyId, setBusyId]     = useState<number | null>(null);
  const [editing, setEditing]   = useState<DetailedCategoryPayload | null>(null);
  const [editId, setEditId]     = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminDetailedCategory | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((c) => {
      if (letter !== "All" && c.letter !== letter) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, letter]);

  const usedLetters = useMemo(() => {
    const set = new Set(data.map((c) => c.letter));
    return LETTERS.filter((l) => set.has(l));
  }, [data]);

  function openCreate() {
    setEditing({ ...EMPTY, letter: letter === "All" ? "A" : letter });
    setEditId(null);
  }

  function openEdit(row: AdminDetailedCategory) {
    setEditing({
      letter: row.letter,
      name: row.name,
      businessCount: row.businessCount,
      sortOrder: row.sortOrder,
      isActive: row.isActive,
    });
    setEditId(row.id);
  }

  function setField<K extends keyof DetailedCategoryPayload>(key: K, value: DetailedCategoryPayload[K]) {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!editing?.name?.trim()) return;
    setBusyId(editId ?? -1);
    try {
      await saveDetailedCategory(accessToken, editing, editId ?? undefined);
      if (editId) {
        setData((prev) => prev.map((c) => (c.id === editId ? { ...c, ...editing, name: editing.name as string } : c)));
      } else {
        setData((prev) => [
          ...prev,
          {
            id: Date.now(),
            letter: (editing.letter as string) || "A",
            name: editing.name as string,
            businessCount: editing.businessCount ?? 0,
            sortOrder: editing.sortOrder ?? prev.length + 1,
            isActive: editing.isActive ?? true,
          },
        ]);
      }
      setEditing(null);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(row: AdminDetailedCategory) {
    const next = !row.isActive;
    setData((prev) => prev.map((c) => (c.id === row.id ? { ...c, isActive: next } : c)));
    await saveDetailedCategory(accessToken, { isActive: next }, row.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteDetailedCategory(accessToken, deleteTarget.id);
      setData((prev) => prev.map((c) => (c.id === deleteTarget.id ? { ...c, isActive: false } : c)));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search A–Z categories…" />
        <select
          value={letter}
          onChange={(e) => setLetter(e.target.value)}
          className={`${adminInput} h-10 w-32 cursor-pointer`}
          aria-label="Filter by letter"
        >
          <option value="All">All letters</option>
          {usedLetters.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New category
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${filtered.length} of ${data.length} categories`}
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-list-ordered-2 text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No categories found</p>
          </div>
        ) : (
          <div className="max-h-[640px] overflow-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead className="sticky top-0 z-10 border-b border-background-200 bg-background-50">
                <tr>
                  <th className={adminTh}>Category</th>
                  <th className={adminTh}>Letter</th>
                  <th className={adminTh}>Listings</th>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Visible</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-background-100/40">
                    <td className={adminTd}>
                      <p className="font-semibold text-foreground-900">{c.name}</p>
                    </td>
                    <td className={adminTd}>
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-secondary-100 px-1.5 text-[11px] font-bold text-secondary-900">
                        {c.letter}
                      </span>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{c.businessCount.toLocaleString("en-IN")}</td>
                    <td className={`${adminTd} tabular-nums`}>{c.sortOrder}</td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => toggleActive(c)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          c.isActive ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {c.isActive ? "Visible" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(c)} className={adminIconBtn} title="Edit category">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(c)} className={adminIconBtn} title="Hide category">
                          <i className="ri-eye-off-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <AdminModal
          title={editId ? "Edit A–Z category" : "New A–Z category"}
          subtitle="Shown on the public A–Z category index"
          onClose={() => setEditing(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!editing.name?.trim() || busyId !== null} onClick={submit} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create category"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="dc-name">Category name</label>
              <input
                id="dc-name"
                type="text"
                value={editing.name ?? ""}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Automobile Repair & Service Centres"
                className={adminInput}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={adminLabel} htmlFor="dc-letter">Letter</label>
                <select
                  id="dc-letter"
                  value={editing.letter ?? "A"}
                  onChange={(e) => setField("letter", e.target.value)}
                  className={`${adminInput} cursor-pointer`}
                >
                  {LETTERS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={adminLabel} htmlFor="dc-count">Listing count</label>
                <input
                  id="dc-count"
                  type="number"
                  min={0}
                  value={editing.businessCount ?? 0}
                  onChange={(e) => setField("businessCount", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="dc-order">Sort order</label>
                <input
                  id="dc-order"
                  type="number"
                  min={0}
                  value={editing.sortOrder ?? 0}
                  onChange={(e) => setField("sortOrder", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input
                type="checkbox"
                checked={!!editing.isActive}
                onChange={(e) => setField("isActive", e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-primary-500"
              />
              Visible on the A–Z index
            </label>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Hide category"
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-eye-off-line text-sm" /> Hide category
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Hide <strong>{deleteTarget.name}</strong> from the public A–Z index? You can make it visible
            again later by toggling its status.
          </p>
        </AdminModal>
      )}
    </div>
  );
}